import React, { useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { IDMCTourOption } from "commonFnBcode/types/dmc.types";
import ImageCropperModal from "./ImageCropperModal";
import { toast } from "react-toastify";
import { activitySearchRtk } from "activityModule/apis/activitySearchRtk";
import DragActivityImage from "./DragActivityImage";

interface CropperState {
  show: boolean;
  imageSrc: string;
  tourOptionId: string | null;
  fileName: string;
}

function TourOptionImageManager(props: { tourOption: IDMCTourOption }) {
  const { tourOption } = props;

  const [images, setImages] = useState<
    { id: number; url: string; sort_no: number }[]
  >([]);
  React.useEffect(() => {
    setImages(
      tourOption.activityImages.map((i) => ({
        id: i.id || 0,
        url: i.image_url,
        sort_no: i.sort_no || 0,
      }))
    );
  }, [tourOption.activityImages]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<number | null>(null);

  const [cropperState, setCropperState] = useState<CropperState>({
    show: false,
    imageSrc: "",
    tourOptionId: null,
    fileName: "",
  });

  const [uploadImage, { isLoading: isUploading }] =
    activitySearchRtk.useAddActivityImageMutation();
  const [deleteImage, { isLoading: isDeleting }] =
    activitySearchRtk.useDeleteActivityImageMutation();

  const handleImageUpload = async (
    tourOptionId: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const imageSrc = URL.createObjectURL(file);

    setCropperState({
      show: true,
      imageSrc,
      tourOptionId,
      fileName: file.name,
    });

    event.target.value = "";
  };

  const handleCropComplete = async (
    croppedImageBlob: Blob,
    croppedImageUrl: string
  ) => {
    if (!cropperState.tourOptionId) return;

    try {
      const formData = new FormData();
      formData.append("image", croppedImageBlob, cropperState.fileName);
      formData.append("tourOptionId", cropperState.tourOptionId);
      formData.append("activityId", tourOption.activityId?.toString() || "");
      const result = await uploadImage(formData).unwrap();

      if (result.success)
        return setImages((prevImages) => [
          ...prevImages,
          {
            url: result.result.imageUrl,
            id: result.result.id,
            sort_no: prevImages.length + 1,
          },
        ]);

      toast.error("Failed to upload image.");
    } catch (error) {
      console.error("Error uploading image:", error);
    } finally {
      toast.success("Image uploaded successfully!");
      setCropperState({
        show: false,
        imageSrc: "",
        tourOptionId: null,
        fileName: "",
      });
    }
  };

  const handleCropperClose = () => {
    if (cropperState.imageSrc) {
      URL.revokeObjectURL(cropperState.imageSrc);
    }
    setCropperState({
      show: false,
      imageSrc: "",
      tourOptionId: null,
      fileName: "",
    });
  };

  const confirmDeleteImage = async () => {
    if (!imageToDelete) return;

    try {
      const result = await deleteImage({
        activityImageId: imageToDelete,
      }).unwrap();
      if (result.success) {
        toast.success("Image deleted successfully!");
        setImages((prevImages) =>
          prevImages.filter((img) => img.id !== imageToDelete)
        );
      } else {
        toast.error("Failed to delete image.");
      }
    } catch (error) {
      console.error("Error deleting image:", error);
    } finally {
      setShowDeleteConfirm(false);
      setImageToDelete(null);
      setImageToDelete(null);
    }
  };

  return (
    <div className="mb-4">
      <div key={tourOption.id} className="mb-4 border rounded p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0 text-secondary">
            <i className="fas fa-route me-2"></i>
            {tourOption.name}
          </h6>
          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-secondary">
              {images?.length || 0} Images
            </span>
            <div className="position-relative">
              <input
                type="file"
                id={`upload-${tourOption.id}`}
                className="d-none"
                multiple
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload((tourOption.id || 0).toString(), e)
                }
                disabled={isUploading}
              />
              <button
                className="btn btn-primary btn-sm btn-flat"
                onClick={() =>
                  document.getElementById(`upload-${tourOption.id}`)?.click()
                }
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-plus me-2"></i>
                    Add Images
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Image Gallery */}
        {images.length > 0 ? (
          <div className="row g-3">
            <DragActivityImage
              images={images}
              imageToDelete={imageToDelete}
              setImageToDelete={setImageToDelete}
              setShowDeleteConfirm={setShowDeleteConfirm}
              isDeleting={isDeleting}
            />
          </div>
        ) : (
          <div className="text-center py-4 bg-light rounded">
            <i className="fas fa-image text-muted fs-1 mb-2"></i>
            <p className="text-muted mb-0">
              No images uploaded for this tour option
            </p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-exclamation-triangle text-warning me-2"></i>
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this image?</p>
          {imageToDelete && (
            <div className="text-center">
              <img
                src={images.find((img) => img.id === imageToDelete)?.url}
                alt="To delete"
                className="img-thumbnail"
                style={{ maxHeight: "200px" }}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary btn-flat"
            onClick={() => setShowDeleteConfirm(false)}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            className="btn btn-danger btn-flat"
            onClick={confirmDeleteImage}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              <>
                <i className="fas fa-trash me-2"></i>
                Delete Image
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Image Cropper Modal */}
      <ImageCropperModal
        show={cropperState.show}
        onHide={handleCropperClose}
        imageSrc={cropperState.imageSrc}
        onCropComplete={handleCropComplete}
        aspectRatio={16 / 9}
        title="Crop Tour Option Image"
      />
    </div>
  );
}

export default TourOptionImageManager;
